/**
 * =============================================================================
 * IDS CRM Web - Engineers Page
 * =============================================================================
 *
 * PURPOSE:
 * List of engineers with status indicators, on-call info, and quick actions.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API_URL, useAuth } from '../context/AuthContext';
import { engineerStatusClass, engineerStatusLabel } from '../utils/status';
import { formatDateKey } from '../utils/dates';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import './Engineers.css';

function Engineers() {
  const { authFetch, user } = useAuth();
  // Coming back from a profile or Time Review (a "←" link or the browser's
  // Back) shows the list as it was left; a fresh visit starts clean.
  const memory = useListMemory('/engineers');

  const [engineers, setEngineers] = useState([]);
  const [onCall, setOnCall] = useState(null);
  const [onCallCovers, setOnCallCovers] = useState([]);
  // Absence register (leave lives in Breathe; management mirrors entries
  // here so scheduling warnings stay accurate until the Breathe sync lands).
  const isManagement = ['admin', 'manager'].includes(user?.role);
  const [absences, setAbsences] = useState([]);
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({ engineer_id: '', absence_type: 'annual_leave', starts_at: '', ends_at: '', notes: '' });
  const [absenceError, setAbsenceError] = useState(null);
  const [absenceSaving, setAbsenceSaving] = useState(false);

  const fetchAbsences = useCallback(async () => {
    if (!['admin', 'manager'].includes(user?.role)) return;
    try {
      const response = await authFetch('/api/v1/engineers/absences');
      if (response.ok) {
        const data = await response.json();
        setAbsences(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch absences:', error);
    }
  }, [authFetch, user?.role]);

  useEffect(() => { fetchAbsences(); }, [fetchAbsences]);

  const recordAbsence = async (event) => {
    event.preventDefault();
    setAbsenceSaving(true);
    setAbsenceError(null);
    try {
      const { engineer_id, ...payload } = absenceForm;
      const response = await authFetch(`/api/v1/engineers/${engineer_id}/absences`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setAbsenceForm({ engineer_id: '', absence_type: 'annual_leave', starts_at: '', ends_at: '', notes: '' });
        setShowAbsenceForm(false);
        fetchAbsences();
      } else {
        const body = await response.json().catch(() => ({}));
        setAbsenceError(body.message || 'Failed to record the absence');
      }
    } catch (error) {
      setAbsenceError('Failed to record the absence');
    } finally {
      setAbsenceSaving(false);
    }
  };

  const removeAbsence = async (absenceId) => {
    const response = await authFetch(`/api/v1/engineers/absences/${absenceId}`, { method: 'DELETE' });
    if (response.ok) fetchAbsences();
  };
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(() => memory.initial('status', ''));
  const [regionFilter, setRegionFilter] = useState(() => memory.initial('region', ''));

  useRememberList('/engineers', { status: statusFilter, region: regionFilter });

  const statuses = [
    { value: '', label: 'All Status' },
    { value: 'available', label: 'Available' },
    { value: 'travelling', label: 'Travelling' },
    { value: 'on_site', label: 'On Site' },
    { value: 'travelling_home', label: 'Travelling Home' },
    { value: 'on_job', label: 'On Job' },
    { value: 'on_call', label: 'On-Call' },
    { value: 'off_duty', label: 'Off Duty' }
  ];

  const fetchEngineers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (regionFilter) params.append('region', regionFilter);

      params.append('include_unassignable', 'true');
      // Include inactive engineers so the greyed "inactive" card styling is
      // actually reachable (they were previously filtered out server-side).
      params.append('include_inactive', 'true');

      const [engineersRes, onCallRes] = await Promise.all([
        authFetch(`/api/v1/engineers?${params}`),
        authFetch('/api/v1/engineers/on-call/current')
      ]);

      const engineersData = await engineersRes.json();
      const onCallData = await onCallRes.json();

      setEngineers(engineersData.data || []);
      setOnCall(onCallData.data);
      setOnCallCovers(onCallData.covers || []);
    } catch (error) {
      console.error('Failed to fetch engineers:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch, statusFilter, regionFilter]);

  useEffect(() => {
    fetchEngineers();
    // Live statuses change as engineers stamp job stages - keep the page fresh
    const interval = setInterval(fetchEngineers, 60000);
    return () => clearInterval(interval);
  }, [fetchEngineers]);

  const getStatusClass = engineerStatusClass;
  const getStatusLabel = engineerStatusLabel;

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  // The chosen region is always offered, even when no engineer shown is in it
  // any more (a list come back to after someone moved region), so it can be
  // seen and chosen away.
  const regions = [...new Set([regionFilter, ...engineers.map(e => e.primary_region)].filter(Boolean))];
  const canEdit = ['admin', 'manager', 'team_leader'].includes(user?.role);
  const canPayroll = user?.role === 'admin';

  return (
    <div className="engineers-page">
      <div className="page-header">
        <div>
          <h1>Engineers</h1>
          <p>Field team status and management</p>
        </div>
        <div className="header-actions">
          <Link to="/on-call" className="btn-secondary">
            On-Call Rota
          </Link>
          {(canPayroll || user?.role === 'manager') && (
            <Link to="/engineers/time-reconciliation" className="btn-secondary">Time Review</Link>
          )}
          {canPayroll && <Link to="/on-call/payroll" className="btn-secondary">Payroll</Link>}
        </div>
      </div>

      {/* On-Call Banner - today's cover from the live rota */}
      {onCall && (
        <div className="on-call-banner">
          <div className="on-call-icon">&#128222;</div>
          <div className="on-call-info">
            <div className="on-call-label">On-Call Today</div>
            <div className="on-call-name">
              {(onCallCovers.length > 0 ? onCallCovers : [onCall]).map(c => (
                <span key={c.assignment_id || c.full_name} className="on-call-cover-entry">
                  {c.full_name}{c.cover_type_label ? ` (${c.cover_type_label})` : ''}
                </span>
              ))}
            </div>
          </div>
          {onCall.mobile && (
            <div className="on-call-contact">
              <a href={`tel:${onCall.mobile}`}>{onCall.mobile}</a>
            </div>
          )}
        </div>
      )}

      {/* Absence register — leave lives in Breathe; entries here feed the
          scheduling warnings until the Breathe API sync lands. */}
      {isManagement && (
        <div className="leave-queue">
          <div className="leave-queue-header">
            <h3>Absence Register</h3>
            <button className="leave-approve" onClick={() => setShowAbsenceForm(prev => !prev)}>
              {showAbsenceForm ? 'Cancel' : 'Record Absence'}
            </button>
          </div>
          <p className="leave-queue-hint">
            Leave is requested and approved in Breathe. Mirror entries here so assignment
            warnings and the engineers&apos; Today screens reflect who is away.
          </p>

          {showAbsenceForm && (
            <form className="leave-record-form" onSubmit={recordAbsence}>
              {absenceError && <div className="form-error">{absenceError}</div>}
              <select
                required
                value={absenceForm.engineer_id}
                onChange={(e) => setAbsenceForm(prev => ({ ...prev, engineer_id: e.target.value }))}
              >
                <option value="">Select engineer…</option>
                {engineers.map(engineer => (
                  <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>
                ))}
              </select>
              <select
                value={absenceForm.absence_type}
                onChange={(e) => setAbsenceForm(prev => ({ ...prev, absence_type: e.target.value }))}
              >
                <option value="annual_leave">Annual leave</option>
                <option value="sick">Sick</option>
                <option value="training">Training</option>
                <option value="compassionate">Compassionate</option>
                <option value="unpaid">Unpaid</option>
              </select>
              <input type="date" required value={absenceForm.starts_at}
                onChange={(e) => setAbsenceForm(prev => ({ ...prev, starts_at: e.target.value }))} />
              <input type="date" required value={absenceForm.ends_at}
                onChange={(e) => setAbsenceForm(prev => ({ ...prev, ends_at: e.target.value }))} />
              <input type="text" placeholder="Note (optional)" value={absenceForm.notes}
                onChange={(e) => setAbsenceForm(prev => ({ ...prev, notes: e.target.value }))} />
              <button type="submit" className="leave-approve" disabled={absenceSaving}>
                {absenceSaving ? 'Saving…' : 'Record'}
              </button>
            </form>
          )}

          {absences.length === 0 ? (
            <p className="leave-queue-hint">No current or upcoming absences recorded.</p>
          ) : (
            absences.map(absence => (
              <div key={absence.absence_id} className="leave-queue-row">
                <div className="leave-queue-body">
                  <strong>{absence.engineer_name}</strong>
                  <span>
                    {absence.absence_type.replace(/_/g, ' ')} · {formatDateKey(absence.starts_at)} – {formatDateKey(absence.ends_at)}
                    {absence.notes ? ` · ${absence.notes}` : ''}
                  </span>
                </div>
                <button className="leave-reject" onClick={() => removeAbsence(absence.absence_id)}>Remove</button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Filters */}
      <div className="engineers-filters">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {statuses.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
        >
          <option value="">All Regions</option>
          {regions.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        <div className="status-summary">
          <span className="summary-item">
            <span className="dot dot-available"></span>
            {engineers.filter(e => e.current_status === 'available').length} Available
          </span>
          <span className="summary-item">
            <span className="dot dot-on-job"></span>
            {engineers.filter(e => e.current_status === 'on_job').length} On Job
          </span>
          <span className="summary-item">
            <span className="dot dot-travelling"></span>
            {engineers.filter(e => e.current_status === 'travelling').length} Travelling
          </span>
        </div>
      </div>

      {/* Engineers Grid */}
      {loading ? (
        <div className="loading">Loading engineers...</div>
      ) : (
        <div className="engineers-grid">
          {engineers.map(engineer => (
            <div
              key={engineer.engineer_id}
              className={`engineer-card ${engineer.is_active === false ? 'inactive' : ''}`}
            >
              <div className="card-header">
                <div className={`avatar ${getStatusClass(engineer.current_status)}`}>
                  {engineer.photo_url ? (
                    <img src={`${API_URL}${engineer.photo_url}`} alt={engineer.full_name} />
                  ) : (
                    getInitials(engineer.full_name)
                  )}
                </div>
                <div className="engineer-info">
                  <div className="engineer-name">{engineer.full_name}</div>
                  <div className="engineer-region">{engineer.primary_region || 'No region'}</div>
                </div>
                <div className={`status-badge ${engineer.is_active === false ? 'status-inactive' : getStatusClass(engineer.current_status)}`}>
                  {engineer.is_active === false ? 'Inactive' : getStatusLabel(engineer.current_status)}
                </div>
              </div>

              <div className="card-stats">
                <div className="stat">
                  <span className="stat-value">{engineer.tasks_today || 0}</span>
                  <span className="stat-label">Tasks Today</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{engineer.open_tickets || 0}</span>
                  <span className="stat-label">Open Tickets</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{engineer.contract_type === 'full_time' ? 'FT' : 'PT'}</span>
                  <span className="stat-label">Contract</span>
                </div>
              </div>

              {engineer.mobile && (
                <div className="card-contact">
                  <span className="contact-icon">&#128222;</span>
                  {engineer.mobile}
                </div>
              )}
              <div className="engineer-card-actions">
                <Link to={`/engineers/${engineer.engineer_id}`} className="btn-secondary">View Profile</Link>
                {canEdit && (
                  <Link to={`/engineers/${engineer.engineer_id}?edit=1`} className="btn-primary">Edit Profile</Link>
                )}
              </div>
            </div>
          ))}

          {engineers.length === 0 && (
            <div className="empty-state">
              No engineers found matching filters
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Engineers;
